import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { actions, useAppStore } from "@/controller";
import { hasAccountIdentity } from "@/auth-identity";
import { useAuth } from "@/models/useAuth";
import { logClientEvent } from "@/telemetry";

export default function DesktopConnectPage() {
  const { t } = useTranslation();
  const token = useAppStore((state) => state.userToken);
  const account = hasAccountIdentity(useAuth.currentAuthUser(token));
  const [deepLink, setDeepLink] = useState<string>();
  const started = useRef(false);
  const request = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const challenge = params.get("challenge") || "";
    const state = params.get("state") || "";
    return /^[A-Za-z0-9_-]{43}$/.test(challenge) && /^[A-Za-z0-9_-]{20,128}$/.test(state)
      ? { challenge, state }
      : undefined;
  }, []);

  useEffect(() => {
    if (!request) {
      logClientEvent("auth.handoffFailed", {
        method: "desktopBrowser",
        reason: "invalidLink",
      });
    }
  }, [request]);

  useEffect(() => {
    if (account || !request) return;
    const returnTo = `${window.location.pathname}${window.location.search}`;
    window.location.replace(
      `/auth?source=desktop-connect&redirectUrl=${encodeURIComponent(returnTo)}`,
    );
  }, [account, request]);

  useEffect(() => {
    if (!account || !request || started.current) return;
    started.current = true;
    void actions.user
      .desktopAuthorize(request.challenge)
      .then((result) => {
        if ("error" in result) {
          logClientEvent("auth.handoffFailed", {
            method: "desktopBrowser",
            reason: "authorization",
          });
          return;
        }
        const url = new URL("onetranscribe-dictate://auth");
        url.searchParams.set("code", result.value);
        url.searchParams.set("state", request.state);
        setDeepLink(url.toString());
        window.location.href = url.toString();
      })
      .catch(() => {
        logClientEvent("auth.handoffFailed", {
          method: "desktopBrowser",
          reason: "authorization",
        });
      });
  }, [account, request]);

  if (!request) {
    return <main className="min-h-screen flex items-center justify-center">{t("desktopConnect.invalidLink")}</main>;
  }
  if (!account) return null;

  return (
    <main className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] flex items-center justify-center px-6">
      <section className="w-full max-w-md text-center">
        <img src="/logo.png" alt="1Transcribe" className="mx-auto mb-6 h-16 w-16 rounded-2xl" />
        <h1 className="text-3xl font-bold tracking-tight">{t("desktopConnect.title")}</h1>
        <p className="mt-3 text-[var(--text-secondary)]">{t("desktopConnect.subtitle")}</p>
        {deepLink ? (
          <a
            href={deepLink}
            className="mt-8 inline-flex min-h-12 items-center justify-center rounded-xl bg-[var(--accent)] px-6 font-semibold text-[var(--text-inverse)]"
          >
            {t("desktopConnect.openApp")}
          </a>
        ) : (
          <p className="mt-8 text-sm text-[var(--text-secondary)]">{t("desktopConnect.connecting")}</p>
        )}
      </section>
    </main>
  );
}
